/**
 * Role catalogue — design doc §11 (abilities, complexity, power) and §12.2 (duplicate rules).
 */

export const TEAMS = ["delegate", "syndicate"] as const;
export type Team = (typeof TEAMS)[number];

/** Catalogue order: Delegate roles (§11.1 table order), then Syndicate roles (§11.2). */
export const ROLE_IDS = [
  "attache",
  "analyst",
  "sentinel",
  "medic",
  "auditor",
  "tracker",
  "archivist",
  "magistrate",
  "confessor",
  "rogue",
  "operative",
  "chameleon",
  "framer",
  "forger",
] as const;
export type RoleId = (typeof ROLE_IDS)[number];

export type Complexity = 1 | 2 | 3;

/**
 * Maximum number of copies of a role, by player count. Each entry `[minPlayers, max]` applies
 * from `minPlayers` upwards; the last matching entry wins.
 */
export type CopyLimits = readonly (readonly [minPlayers: number, max: number])[];

export interface RoleDef {
  readonly id: RoleId;
  readonly name: string;
  readonly team: Team;
  readonly complexity: Complexity;
  /** Setup-builder power (§13.2). */
  readonly power: number;
  /** Optional roles must be switched on in the lobby (§12.2 rule 5). */
  readonly optional: boolean;
  /** Name of the role card the role adds to the hand, if any. */
  readonly card: string | null;
  /** One-line ability text for the UI. */
  readonly ability: string;
  /** Duplicate rules from §12.2 under the standard duplicate setting. */
  readonly copyLimits: CopyLimits;
}

const UNIQUE: CopyLimits = [[0, 1]];
const UNIQUE_BELOW_15: CopyLimits = [
  [0, 1],
  [15, 2],
];

export const ROLES: Readonly<Record<RoleId, RoleDef>> = {
  attache: {
    id: "attache",
    name: "Attaché",
    team: "delegate",
    complexity: 1,
    power: 0,
    optional: false,
    card: null,
    ability: "No special ability. You may Seal twice per round.",
    copyLimits: [[0, Number.POSITIVE_INFINITY]],
  },
  analyst: {
    id: "analyst",
    name: "Analyst",
    team: "delegate",
    complexity: 1,
    power: 3,
    optional: false,
    card: null,
    ability: "At Dusk, learn how many of this round's partners are Agents (needs 2+ handshakes).",
    copyLimits: UNIQUE_BELOW_15,
  },
  sentinel: {
    id: "sentinel",
    name: "Sentinel",
    team: "delegate",
    complexity: 1,
    power: 1,
    optional: false,
    card: null,
    ability: "At Dusk, learn which Syndicate cards were played on you this round (types only).",
    copyLimits: UNIQUE,
  },
  medic: {
    id: "medic",
    name: "Medic",
    team: "delegate",
    complexity: 2,
    power: 2,
    optional: false,
    card: "inoculate",
    ability: "Inoculate a partner: cures Poison and blocks Siphons on them this round.",
    copyLimits: UNIQUE_BELOW_15,
  },
  auditor: {
    id: "auditor",
    name: "Auditor",
    team: "delegate",
    complexity: 2,
    power: 2,
    optional: false,
    card: "audit",
    ability: "Audit a partner: learn how many Syndicate cards they played this round.",
    copyLimits: UNIQUE_BELOW_15,
  },
  tracker: {
    id: "tracker",
    name: "Tracker",
    team: "delegate",
    complexity: 2,
    power: 2,
    optional: false,
    card: "tail",
    ability: "Tail a partner: learn everyone they shook hands with this round.",
    copyLimits: UNIQUE,
  },
  archivist: {
    id: "archivist",
    name: "Archivist",
    team: "delegate",
    complexity: 1,
    power: 1,
    optional: false,
    card: null,
    ability: "After each exile, privately learn the exiled player's team.",
    copyLimits: UNIQUE,
  },
  magistrate: {
    id: "magistrate",
    name: "Magistrate",
    team: "delegate",
    complexity: 1,
    power: 1,
    optional: false,
    card: null,
    ability: "You may cast a public Gavel ballot that counts as 2 votes.",
    copyLimits: UNIQUE,
  },
  confessor: {
    id: "confessor",
    name: "Confessor",
    team: "delegate",
    complexity: 3,
    power: 2,
    optional: true,
    card: "confide",
    ability: "Once per game, Confide: you and your partner learn each other's true team.",
    copyLimits: UNIQUE,
  },
  rogue: {
    id: "rogue",
    name: "Rogue",
    team: "delegate",
    complexity: 2,
    power: -1,
    optional: true,
    card: null,
    ability: "You register as an Agent to Analysts and Auditors. You may Seal twice per round.",
    copyLimits: UNIQUE,
  },
  operative: {
    id: "operative",
    name: "Operative",
    team: "syndicate",
    complexity: 1,
    power: 1,
    optional: false,
    card: null,
    ability: "You may Siphon twice per round, on different partners.",
    copyLimits: [
      [0, 1],
      [15, 2],
      [18, 3],
    ],
  },
  chameleon: {
    id: "chameleon",
    name: "Chameleon",
    team: "syndicate",
    complexity: 2,
    power: 2,
    optional: false,
    card: null,
    ability: "You register as a Delegate to Analysts and Auditors.",
    copyLimits: UNIQUE,
  },
  framer: {
    id: "framer",
    name: "Framer",
    team: "syndicate",
    complexity: 2,
    power: 2,
    optional: false,
    card: "plant",
    ability: "Plant a partner: they register as an Agent this round.",
    copyLimits: UNIQUE,
  },
  forger: {
    id: "forger",
    name: "Forger",
    team: "syndicate",
    complexity: 3,
    power: 3,
    optional: false,
    card: "bug",
    ability: "Bug a partner: their cards are voided and their reports scrambled this round.",
    copyLimits: UNIQUE,
  },
};

export const DELEGATE_ROLE_IDS: readonly RoleId[] = ROLE_IDS.filter(
  (id) => ROLES[id].team === "delegate",
);
export const SYNDICATE_ROLE_IDS: readonly RoleId[] = ROLE_IDS.filter(
  (id) => ROLES[id].team === "syndicate",
);
export const OPTIONAL_ROLE_IDS: readonly RoleId[] = ROLE_IDS.filter((id) => ROLES[id].optional);

/** The role that fills every remaining Delegate seat and may always repeat. */
export const FILLER_ROLE: RoleId = "attache";

/**
 * How duplicate roles are handled (advanced setting "allow duplicate roles", §14.4):
 * - `standard`: the §12.2 rules (Attaché repeats; Analyst/Auditor/Medic twice at 15+;
 *   Operative twice at 15+ and three times at 18+).
 * - `off`: every role is unique, except the Attaché.
 * - `unlimited`: any role may repeat.
 */
export const DUPLICATE_MODES = ["standard", "off", "unlimited"] as const;
export type DuplicateMode = (typeof DUPLICATE_MODES)[number];

export function maxCopies(
  role: RoleId,
  playerCount: number,
  mode: DuplicateMode = "standard",
): number {
  if (role === FILLER_ROLE || mode === "unlimited") return Number.POSITIVE_INFINITY;
  if (mode === "off") return 1;
  let max = 0;
  for (const [minPlayers, limit] of ROLES[role].copyLimits) {
    if (playerCount >= minPlayers) max = limit;
  }
  return max;
}

export function isRoleId(value: unknown): value is RoleId {
  return typeof value === "string" && (ROLE_IDS as readonly string[]).includes(value);
}

/** Sorts role ids into catalogue order (roles are multisets; order carries no meaning). */
export function sortRoles(roles: readonly RoleId[]): RoleId[] {
  return [...roles].sort((a, b) => ROLE_IDS.indexOf(a) - ROLE_IDS.indexOf(b));
}

/** Counts each role in a list. */
export function countRoles(roles: readonly RoleId[]): Map<RoleId, number> {
  const counts = new Map<RoleId, number>();
  for (const role of roles) counts.set(role, (counts.get(role) ?? 0) + 1);
  return counts;
}
